import { useLazyQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { FinanceInsurancePerformanceReport, RevenueDealStatusFilter } from '@trnsact/trnsact-shared-types';
import { useNotifications } from 'modules/notification';
import { FINANCE_INSURANCE_PERFORMANCE_REPORT } from '../api';
import { PerformanceEnrichedRow, FinanceDateRange, PerformanceFiltersState } from '../types';

function dateRangeToCreatedAfter(range: FinanceDateRange): string {
  switch (range) {
    case 'cm':
      return moment().startOf('month').format('YYYY-MM-DD');
    case 'lm':
      return moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    case 'cq':
      return moment().startOf('quarter').format('YYYY-MM-DD');
    case 'lq':
      return moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD');
    case '30':
      return moment().subtract(30, 'days').format('YYYY-MM-DD');
    case '90':
      return moment().subtract(90, 'days').format('YYYY-MM-DD');
    case '180':
      return moment().subtract(180, 'days').format('YYYY-MM-DD');
    case '365':
      return moment().subtract(365, 'days').format('YYYY-MM-DD');
  }
}

export function useFinancePerformanceData(filters: PerformanceFiltersState) {
  const { showNotification } = useNotifications();

  const [fetchReport, { data, loading }] = useLazyQuery<{
    financeInsurancePerformanceReport: FinanceInsurancePerformanceReport;
  }>(FINANCE_INSURANCE_PERFORMANCE_REPORT, {
    notifyOnNetworkStatusChange: true,
    onError: () => showNotification('Failed to load performance data', { type: 'error' }),
  });

  const input = useMemo(
    () => ({
      groupBy: filters.groupBy,
      createdAfter: dateRangeToCreatedAfter(filters.dateRange),
      filters: {
        paymentType: filters.paymentType,
        ...(filters.dealStatus !== 'ALL' && { dealStatus: filters.dealStatus as RevenueDealStatusFilter }),
      },
    }),
    [filters]
  );

  useEffect(() => {
    fetchReport({ variables: { input } });
  }, [input, fetchReport]);

  const rows = useMemo((): PerformanceEnrichedRow[] => {
    const apiRows = data?.financeInsurancePerformanceReport?.rows ?? [];
    return apiRows.map(row => ({
      name: row.name,
      deals: row.deals,
      fiDeals: row.fiDeals,
      financePenetration: row.financePenetration,
      escPenetration: row.escPenetration,
      insurancePenetration: row.insurancePenetration,
      gapPenetration: row.gapPenetration,
      roadsidePenetration: row.roadsidePenetration,
      financeProfit: row.financeProfit,
      escProfit: row.escProfit,
      insuranceProfit: row.insuranceProfit,
      gapProfit: row.gapProfit,
      roadsideProfit: row.roadsideProfit,
      totalProfit: row.totalProfit,
      avgProfitPerAllDeals: row.avgProfitPerDeal,
      avgProfitPerFIDeals: row.avgProfitPerFiDeals,
    }));
  }, [data]);

  return { rows, loading };
}
